import type { SyncPullChanges } from '@pms/shared';

import { pullSince } from './api.js';
import { db, getDeviceId, getMeta } from './db.js';
import { META_LAST_PULL_AT, META_PULL_CURSOR } from './types.js';

/**
 * Входящая синхронизация: справочники, график и назначения.
 *
 * Курсор хранится локально. Данные и курсор пишутся одной транзакцией —
 * иначе при обрыве посередине курсор уехал бы вперёд, а часть строк не легла,
 * и они не приехали бы уже никогда.
 */

function countChanges(changes: SyncPullChanges): number {
  return Object.values(changes).reduce((sum, rows) => sum + rows.length, 0);
}

export async function applyChanges(changes: SyncPullChanges, cursor: string): Promise<void> {
  await db.transaction(
    'rw',
    [
      db.sites,
      db.machines,
      db.shiftTemplates,
      db.productSizes,
      db.materialTypes,
      db.recipes,
      db.siteNorms,
      db.machineNorms,
      db.appSettings,
      db.users,
      db.shifts,
      db.shiftAssignments,
      db.dayOffWishes,
      db.absences,
      db.notifications,
      db.meta,
    ],
    async () => {
      if (changes.sites.length) await db.sites.bulkPut(changes.sites);
      if (changes.machines.length) await db.machines.bulkPut(changes.machines);
      if (changes.shiftTemplates.length) await db.shiftTemplates.bulkPut(changes.shiftTemplates);
      if (changes.productSizes.length) await db.productSizes.bulkPut(changes.productSizes);
      if (changes.materialTypes.length) await db.materialTypes.bulkPut(changes.materialTypes);
      if (changes.recipes.length) await db.recipes.bulkPut(changes.recipes);
      if (changes.siteNorms.length) await db.siteNorms.bulkPut(changes.siteNorms);
      if (changes.machineNorms.length) await db.machineNorms.bulkPut(changes.machineNorms);
      if (changes.appSettings.length) await db.appSettings.bulkPut(changes.appSettings);
      if (changes.users.length) await db.users.bulkPut(changes.users);
      if (changes.shifts.length) await db.shifts.bulkPut(changes.shifts);
      if (changes.shiftAssignments.length) {
        await db.shiftAssignments.bulkPut(changes.shiftAssignments);
      }
      if (changes.dayOffWishes.length) await db.dayOffWishes.bulkPut(changes.dayOffWishes);
      if (changes.absences.length) await db.absences.bulkPut(changes.absences);
      if (changes.notifications.length) await db.notifications.bulkPut(changes.notifications);
      await db.meta.put({ key: META_PULL_CURSOR, value: cursor });
      await db.meta.put({ key: META_LAST_PULL_AT, value: new Date().toISOString() });
    },
  );
}

export interface PullResult {
  applied: number;
  cursor: string;
  hasMore: boolean;
}

export async function pullOnce(): Promise<PullResult> {
  const deviceId = await getDeviceId();
  const cursor = (await getMeta<string>(META_PULL_CURSOR)) ?? '0';
  const response = await pullSince(cursor, deviceId);
  await applyChanges(response.changes, response.cursor);
  return {
    applied: countChanges(response.changes),
    cursor: response.cursor,
    hasMore: response.hasMore,
  };
}

/** Забрать всё, что накопилось, порциями. Ограничение по числу порций — от зацикливания. */
export async function pullAll(maxPages = 50): Promise<number> {
  let applied = 0;
  for (let page = 0; page < maxPages; page += 1) {
    const result = await pullOnce();
    applied += result.applied;
    if (!result.hasMore) break;
  }
  return applied;
}
